import { ApiError, apiErrorReason } from '@/lib/api';

import { ROLE_COLUMNS } from './columns';
import type { ImportRowInput, SheetProblem } from './types';
import { errorAdvice } from '@/lib/error-advice';

// Diagnósticos de la migración en palabras (UX-COPY-01): qué pasa con la fila y, si
// bloquea, qué corregir en el Excel (UX-ERR-01).

const ISSUES: Record<string, string> = {
  code_already_exists: 'El código ya existe en la empresa y los códigos no se reutilizan.',
  duplicate_code_in_batch: 'El código se repite en el archivo.',
  type_missing_frequency: 'El tipo de documento no existe: indica su nombre y su frecuencia.',
  vigente_without_date: 'Vigente sin fecha: su próxima revisión se fija después.',
  vigente_without_elaborador: 'Vigente sin elaborador: asígnalo después desde la ficha.',
  norm_not_found: 'Una norma no está en el catálogo: se omite.',
  unknown_custom_field: 'Un campo propio no existe en la empresa.',
  custom_field_missing: 'Falta un campo propio obligatorio.',
  invalid_custom_field: 'Un campo propio tiene un valor no válido.',
  person_not_member: 'Una persona no pertenece a esta empresa.',
  person_not_found:
    'Un correo no corresponde a nadie con acceso al QMS en la empresa: se omite. Si está en el LMS, agrégalo en Usuarios y vuelve a previsualizar.',
  position_not_found: 'Un puesto no existe en esta empresa: se omite.',
  owner_not_member: 'El dueño no pertenece a esta empresa.',
  owner_not_found:
    'El correo del dueño no corresponde a nadie con acceso al QMS en la empresa: se omite. Si está en el LMS, agrégalo en Usuarios y vuelve a previsualizar.',
};

export function issueText(issue: { level: string; reason: string }): string {
  return ISSUES[issue.reason] ?? 'Revisa esta fila.';
}

export function importError(err: unknown): string {
  const reason = apiErrorReason(err);
  if (reason === 'import_has_errors') {
    return 'Algunos documentos cambiaron mientras revisabas y ahora tienen errores. Vuelve a previsualizar.';
  }
  if (reason === 'import_conflict') {
    return 'Otra carga tomó alguno de estos códigos al mismo tiempo. Vuelve a previsualizar.';
  }
  if (reason === 'lms_unavailable') {
    return 'El LMS no responde y no se pudieron validar los puestos. Intenta en unos minutos.';
  }
  if (err instanceof ApiError && err.status === 403) {
    return 'No tienes permiso para cargar datos históricos. Pide acceso al Administrador de Calidad.';
  }
  if (err instanceof ApiError && err.status === 400) {
    return 'El archivo tiene valores que el sistema no acepta. Revisa los formatos de la plantilla.';
  }
  return `No se pudo completar. ${errorAdvice(err)}`;
}

/** Avisos que dejan un rol sin asignar: se completan después desde la ficha. */
export const UNASSIGNED_REASONS = new Set([
  'person_not_found',
  'person_not_member',
  'position_not_found',
  'vigente_without_elaborador',
]);

const FIELD_COLUMN: Record<string, string> = {
  areaPath: 'Ruta de proceso',
  areaNames: 'Nombres de proceso',
  documentTypeCode: 'Tipo de documento',
  documentTypeName: 'Nombre del tipo',
  reviewFrequencyDays: 'Frecuencia del tipo (días)',
  code: 'Código',
  title: 'Nombre',
  origin: 'Origen',
  status: 'Estado',
  currentVersion: 'Versión actual',
  revisionLabel: 'Revisión',
  effectiveDate: 'Fecha de vigencia',
  approvedAt: 'Fecha de aprobación',
  reviewFrequencyDaysOverride: 'Frecuencia propia (días)',
  norms: 'Normas',
  ownerEmail: 'Dueño',
};
const ROLE_COLUMN = new Map<string, string>(ROLE_COLUMNS.map(([column, role]) => [role, column]));
const SERVER_REJECTED = 'El sistema no acepta este valor: revisa el formato o el largo.';
const ISSUE_PATH = /^rows\.(\d+)\.(\w+)(?:\.(\d+))?/;

/**
 * Un 400 del servidor trae la ruta de cada valor rechazado (`rows.12.title`): se
 * traduce a fila del Excel y columna, para que Calidad encuentre la celda (UX-ERR-01).
 */
export function serverProblems(
  err: unknown,
  rows: readonly ImportRowInput[],
  rowNumbers: readonly number[],
): SheetProblem[] {
  const issues = err instanceof ApiError && err.status === 400 ? err.meta?.['issues'] : undefined;
  if (!Array.isArray(issues)) {
    return [];
  }
  const seen = new Set<string>();
  const out: SheetProblem[] = [];
  for (const issue of issues as { path?: unknown }[]) {
    const m = ISSUE_PATH.exec(typeof issue.path === 'string' ? issue.path : '');
    const index = m ? Number(m[1]) : -1;
    const field = m?.[2] ?? '';
    const role =
      field === 'responsibilities'
        ? rows[index]?.responsibilities?.[Number(m?.[3])]?.role
        : undefined;
    const column = (role && ROLE_COLUMN.get(role)) ?? FIELD_COLUMN[field] ?? null;
    const row = m ? (rowNumbers[index] ?? index + 2) : null;
    const key = `${row}|${column}`;
    if (!seen.has(key)) {
      seen.add(key);
      out.push({ row, column, message: SERVER_REJECTED });
    }
  }
  return out;
}
